import React, { useCallback, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";

import { IconButton } from "@components/atoms/buttons/icon-button/icon-button";
import { ChildType, EntryType } from "@common/types/tree-types";

import checkBoxIcon from "../../icons/checkbox-icon.svg";
import checkBoxCheckedIcon from "../../icons/checked-checkbox-icon.svg";
import transferredCheckboxIcon from "@src/assets/svg/transferred-checkbox-icon.svg";

import styles from "../TreeComponents.module.scss";
import { dropInitialListToExistingOurNodeMachineStructure } from "./use-cases/drop-initial-list-to-existing-our-node-machine-structure";

import { deactivateAllAreaOverlays } from "../../use-cases/deactivate-all-drop-area-overlays";
import { FlatListProvider } from "@src/cloud-connect/node-manager/flat-list-provider";
import { isDraggable } from "../../use-cases/is-draggable";
import { OurNode } from "@src/cloud-connect/poc/our-node";
import { TreeOurNodeObj } from "./tree-our-node-obj";
import { manageUnassignedOurNodeViewInBlueprintMode } from "./use-cases/manage-unassigned-our-node-view-in-blueprint-mode";
import { NameOurNode } from "./name-our-node";
import { CaretOurNode } from "./caret-our-node";
import { useCaretOurNodeInitialHook } from "./use-caret-our-node-initial-hook";
import { useCaretOurNodeMachineHook } from "./use-caret-our-node-machine-hook";
import { handleDragStartOurNode } from "./use-cases/handle-drag-start-our-node";
import { dropMachineListToExistingOurNodeMachineStructure } from "./use-cases/drop-machine-list-to-existing-our-node-machine-structure";
import { dropInitialListInMachineStructureFolder } from "./use-cases/drop-initial-list-to-our-node-machine-structure-folder";
import { dropMachineListInMachineStructureFolder } from "./use-cases/drop-machine-list-to-our-node-machine-structure-folder";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { activateMachineStructureDropAreaOverlay } from "../../use-cases/activate-machine-structure-drop-area-overlay";
import {
  emptyNodeDataObject,
  updateActiveMachineStructureNode,
} from "@src/reducers/your-reducer";
import { writeEmptyNodeDetails } from "./use-cases/write-node-details-our-node";

export interface FlatChildTypes {
  id: string;
  name: string;
  key: string;
  children?: ChildType[] | EntryType[] | undefined;
  amountOfSubNodes: number;
  selected: boolean;
  node_type?: string;
  parent;
  visible?: boolean;
  expanded?: boolean;
  transferred?: boolean;
  unassigned?: boolean;
}

type TreeProps = {
  child: OurNode;
  type: "initial" | "machine";
  list?: ChildType[];
  activeId?: string;
  useHook: () => TreeOurNodeObj;
};

export const TreeOurNode = ({ child, type, list, useHook }: TreeProps) => {
  const treeOurNodeObj = useHook();
  const dispatch = useDispatch();

  const activeNodeId = useSelector((state: RootState) =>
    type === "initial"
      ? state.yourSlice.activeInitialStructureNode
      : state.yourSlice.activeMachineStructureNode,
  );

  const [isDraggingAndHoveringOverNode, setIsDraggingAndHoveringOverNode] =
    useState(false);
  const [
    isDraggingAndHoveringOverInsertionLine,
    setIsDraggingAndHoveringOverInsertionLine,
  ] = useState(false);

  const returnCheckboxIconByState = (selected: boolean) => {
    return selected ? checkBoxCheckedIcon : checkBoxIcon;
  };

  const childNameIsRoot = (childName) => {
    return childName === "Root";
  };

  const handleOnDrop = (e, child, isItInsertionLine) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAndHoveringOverNode(false);
    setIsDraggingAndHoveringOverInsertionLine(false);
    dispatch(updateActiveMachineStructureNode(""));
    writeEmptyNodeDetails(dispatch, emptyNodeDataObject);
    FlatListProvider.getInstance().deleteFlatMachineStructureList();

    if (type === "initial") return;
    if (childNameIsRoot(child.name)) return;

    const dragType = e.dataTransfer.getData("text/plain");

    if (dragType === "initial") {
      if (child.node_type === "FOLDER" && !isItInsertionLine) {
        dropInitialListInMachineStructureFolder(child);
      } else {
        dropInitialListToExistingOurNodeMachineStructure(child);
      }
    } else {
      if (child.node_type === "FOLDER" && !isItInsertionLine) {
        dropMachineListInMachineStructureFolder(child);
      } else {
        dropMachineListToExistingOurNodeMachineStructure(child);
      }
    }

    deactivateAllAreaOverlays();
  };

  const handleNodeDragOver = useCallback(
    (e) => {
      if (type === "initial") return;
      if (childNameIsRoot(child.name)) return;
      e.preventDefault();
      e.stopPropagation();
      if (child.node_type !== "FOLDER") {
        setIsDraggingAndHoveringOverInsertionLine(true);
        setIsDraggingAndHoveringOverNode(false);
      } else {
        setIsDraggingAndHoveringOverInsertionLine(false);
        setIsDraggingAndHoveringOverNode(true);
      }
    },
    [type, child.node_type, child.name],
  );

  const handleNodeDragLeave = useCallback((e) => {
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setIsDraggingAndHoveringOverNode(false);
    setIsDraggingAndHoveringOverInsertionLine(false);
  }, []);

  const handleInsertionLineDragOver = useCallback(
    (e) => {
      if (type === "initial") return;
      if (childNameIsRoot(child.name)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingAndHoveringOverInsertionLine(true);
      setIsDraggingAndHoveringOverNode(false);
    },
    [type, child.name],
  );

  const handleInsertionLineDragLeave = useCallback((e) => {
    e.stopPropagation();
    setIsDraggingAndHoveringOverInsertionLine(false);
  }, []);

  const handleOnDragEnd = () => {
    deactivateAllAreaOverlays();
  };

  // render checkbox
  const renderCheckBoxButton = () => {
    if (child.transfered && type === "initial") {
      return (
        <IconButton
          disabled={true}
          icon={transferredCheckboxIcon}
          fun={() => ({})}
          variant={"no-padding"}
        />
      );
    }

    /* if (
      child.unassigned &&
      type === "machine" &&
      child.node_type !== "FOLDER" &&
      !child.selected
    ) {
      return (
        <IconButton
          icon={checkBoxIconWarning}
          fun={() => handleCheckBoxClick(child, updateCheckboxFunc)}
          variant={"no-padding"}
        />
      );
    } */

    return (
      <IconButton
        icon={returnCheckboxIconByState(child.selected)}
        fun={() => treeOurNodeObj.handleCheckBoxClick(child)}
        variant={"no-padding"}
      />
    );
  };

  const defineActionOnDrag = (e) => {
    handleDragStartOurNode(e, type);

    if (getMachineStructureNodeset().getFilteredFlatMap().size > 0) {
      activateMachineStructureDropAreaOverlay();
    }
  };

  return (
    <li
      className={styles["tree-node"]}
      key={child.key}
      onDragStart={(e) => defineActionOnDrag(e)}
      draggable={isDraggable(child)}
      onDrop={(e) => handleOnDrop(e, child, false)}
      onDragEnd={handleOnDragEnd}
    >
      <span
        className={cn(styles["tree-node__parent"], {
          [styles["tree-node__parent--selected"]]: activeNodeId === child.key,
          [styles["tree-node__folder-hover-active"]]:
            isDraggingAndHoveringOverNode,
          /* [styles["tree-node__parent--unassigned"]]: child.unassigned, */
        })}
        onDragOver={handleNodeDragOver}
        onDragLeave={handleNodeDragLeave}
      >
        <span className={styles["tree-node__checkbox-name-wrapper"]}>
          <span className={styles["tree-node__checkbox-wrapper"]}>
            {renderCheckBoxButton()}
          </span>
          <NameOurNode
            child={child}
            type={type}
            list={list}
            transferred={type === "initial" && child.transfered}
          />
        </span>
        {manageUnassignedOurNodeViewInBlueprintMode(child, type)}
        <CaretOurNode
          child={child}
          useHook={
            type === "initial"
              ? useCaretOurNodeInitialHook
              : useCaretOurNodeMachineHook
          }
        />
      </span>
      {type === "machine" && (
        <div
          className={cn(styles["tree-node__insertion-line"], {
            [styles["tree-node__insertion-line-hover-active"]]:
              isDraggingAndHoveringOverInsertionLine,
          })}
          onDragOver={handleInsertionLineDragOver}
          onDragLeave={handleInsertionLineDragLeave}
          onDrop={(e) => handleOnDrop(e, child, true)}
        />
      )}
    </li>
  );
};
